/**
 * V2 Stepper Section Configuration
 *
 * Maps V2 StepNode.content.stepper paths for the ProgressSteps screen type.
 *
 * V2 StepperConfig:
 * - kind: StepperKind
 * - timer: Countdown
 * - items: Array<StepperSegment> where Segment = { progressStart, progressEnd, content, timer? }
 * - label: StepperLabel
 * - appearance: StepperStyle
 *
 * Sprint: schema-v2-migration, Sprint 4
 */

import type { ProgressBarItemVisibility } from '../types/progressBarSection.types';

/**
 * Valid StepperKind values from V2 schema.
 * Must match StepperKind enum: orbital, rail, multiRail, stepperSlot1-5
 */
export const V2_STEPPER_KIND_OPTIONS = [
  { value: 'orbital', label: 'Circle' },
  { value: 'imageContainerFirst', label: 'Image Container First' },
  { value: 'chartContainerFirst', label: 'Chart Container First' },
];

export const DEFAULT_STEPPER_TIMER_DURATION = 10;

/**
 * Default new segment structure for V2 stepper
 */
export function createDefaultV2Segment(locale: string, progressStart: number, progressEnd: number) {
  return {
    progressStart,
    progressEnd,
    content: {
      title: {
        translations: { [locale]: '' },
        kind: 'static',
        templateParams: { labels: {}, links: {}, countdowns: {} },
        appearance: {},
      },
      subtitle: {
        translations: { [locale]: '' },
        kind: 'static',
        templateParams: { labels: {}, links: {}, countdowns: {} },
        appearance: {},
      },
      description: {
        translations: { [locale]: '' },
        kind: 'static',
        templateParams: { labels: {}, links: {}, countdowns: {} },
        appearance: {},
      },
      image: { translations: {} },
    },
  };
}

/**
 * Convert progressStart/progressEnd array to duration percentages
 */
export function segmentsToDurations(items: Array<{ progressStart: number; progressEnd: number }>): number[] {
  return items.map((item) => (item.progressEnd ?? 0) - (item.progressStart ?? 0));
}

/**
 * Convert duration percentages back to progressStart/progressEnd
 */
export function durationsToSegmentRanges(durations: number[]): Array<{ progressStart: number; progressEnd: number }> {
  const ranges: Array<{ progressStart: number; progressEnd: number }> = [];
  let cumulative = 0;
  for (const d of durations) {
    ranges.push({ progressStart: cumulative, progressEnd: cumulative + d });
    cumulative += d;
  }
  return ranges;
}

/**
 * Get visibility configuration for stepper kind.
 * Controls which fields are shown in ProgressBarItemEditor.
 * Pattern copied from progressBarSectionConfig.ts getProgressBarVisibility().
 */
export function getStepperVisibility(kind: string): ProgressBarItemVisibility {
  return {
    showImage: kind !== 'orbital',
    showTitle: true,
    showSubtitle: true,
    showDescription: true,
  };
}

/**
 * Valid subtype options for HeroContent screen.
 * Controls image position relative to text (screen layout, not image styling).
 */
export const V2_HERO_SUBTYPE_OPTIONS = [
  { value: 'heroSlot1', label: 'Image till navbar' },
  { value: 'heroSlot2', label: 'Image till top' },
  { value: 'heroSlot3', label: 'Image after text' },
  { value: 'heroSlot4', label: 'Between title/subtitle' },
];
